import { prepMinutes, servesCount, spiceLevel } from '@/lib/menuFacts';
import type { MenuItemOut, MenuOut, RecommendationLaneKey, RecommendationsOut } from '@/types/api';

export interface RecommendationParams {
  limit?: number;
  budget_irr?: number;
  party_size?: number;
  dietary?: 'any' | 'vegetarian' | 'vegan' | 'gluten_free';
  spice_max?: number;
  max_prep_minutes?: number;
  cart_item_ids?: string;
}

const seasonLabels = { spring: 'بهار', summer: 'تابستان', autumn: 'پاییز', winter: 'زمستان' } as const;
const weekdays = ['دوشنبه', 'سه‌شنبه', 'چهارشنبه', 'پنج‌شنبه', 'جمعه', 'شنبه', 'یک‌شنبه'] as const;

function context(now = new Date()): RecommendationsOut['context'] {
  const hour = now.getHours();
  const daypart = hour >= 6 && hour < 11 ? 'breakfast' : hour < 16 ? 'lunch' : hour < 21 ? 'evening' : 'late';
  const daypartLabel = {
    breakfast: 'صبحانه و شروع روز',
    lunch: 'ناهار و غذای اصلی',
    evening: 'عصرانه و شام',
    late: 'شب و جمع‌بندی شیرین',
  } as const;
  const month = now.getMonth() + 1;
  const monthDay = month * 100 + now.getDate();
  const season = monthDay >= 321 && monthDay < 621 ? 'spring' : monthDay >= 621 && monthDay < 923 ? 'summer' : monthDay >= 923 && monthDay < 1221 ? 'autumn' : 'winter';
  const jsWeekday = now.getDay();
  const mondayIndex = (jsWeekday + 6) % 7;
  return {
    daypart,
    daypart_label_fa: daypartLabel[daypart],
    season,
    season_label_fa: seasonLabels[season],
    weekday_label_fa: weekdays[mondayIndex] ?? weekdays[0],
    local_hour: hour,
    generated_at: now.toISOString(),
  };
}

const DRINK_OR_DESSERT = ['نوشیدنی', 'دسر', 'قهوه', 'چای', 'شیک', 'ماکتیل', 'شربت', 'آیس کافی', 'کافیین', 'کیک'];
const SIDES = ['سالاد', 'سیب زمینی', 'متفرقه'];
const isDrinkOrDessert = (name: string) => DRINK_OR_DESSERT.some((word) => name.includes(word));
const isMain = (name: string) => !isDrinkOrDessert(name) && !name.includes('صبحانه') && !SIDES.some((word) => name.includes(word));

function category(item: MenuItemOut, menu: MenuOut): string {
  return menu.categories.find((entry) => entry.id === item.category_id)?.name_fa ?? '';
}

function dietMatches(item: MenuItemOut, dietary: RecommendationParams['dietary']): boolean {
  const tags = new Set(item.dietary_tags);
  if (dietary === 'vegetarian') return ['گیاهی', 'گیاه‌خواری', 'وگان'].some((tag) => tags.has(tag));
  if (dietary === 'vegan') return tags.has('وگان');
  if (dietary === 'gluten_free') return tags.has('بدون گلوتن');
  return true;
}

function momentPool(items: MenuItemOut[], menu: MenuOut, contextValue: RecommendationsOut['context']): MenuItemOut[] {
  const breakfast = (item: MenuItemOut) => category(item, menu).includes('صبحانه');
  const drinkOrDessert = (item: MenuItemOut) => isDrinkOrDessert(category(item, menu));
  const timely = contextValue.daypart === 'breakfast'
    ? items.filter((item) => breakfast(item) || drinkOrDessert(item))
    : items.filter((item) => !breakfast(item));
  return timely.length ? timely : items;
}

function rank(items: MenuItemOut[], menu: MenuOut, key: RecommendationLaneKey, contextValue: RecommendationsOut['context'], limit: number, partySize: number): MenuItemOut[] {
  const score = (item: MenuItemOut) => {
    const categoryName = category(item, menu);
    let value = (item.featured ? 2 : 0) + (item.popularity_label ? 2.5 : 0) + (item.seasonal_offer ? 1.5 : 0);
    if (contextValue.daypart === 'breakfast' && categoryName.includes('صبحانه')) value += 4;
    if (contextValue.daypart === 'lunch' && isMain(categoryName)) value += 3;
    if (contextValue.daypart !== 'breakfast' && !categoryName.includes('صبحانه')) value += 2;
    if (contextValue.daypart === 'late' && isDrinkOrDessert(categoryName)) value += 3;
    const serves = servesCount(item);
    if (serves !== null) {
      if ((contextValue.weekday_label_fa === 'پنج‌شنبه' || contextValue.weekday_label_fa === 'جمعه') && serves >= 2) value += 1.6;
      if (partySize === 1 && serves === 1) value += 1;
      if (partySize > 1 && serves >= partySize) value += 2;
    }
    const minutes = prepMinutes(item);
    if (key === 'quick' && minutes !== null) value += Math.max(0, 6 - minutes / 3);
    if (key === 'popular') value += item.popularity_label ? 4 : 0;
    if (key === 'seasonal') value += item.seasonal_offer ? 7 : -4;
    if (key === 'pairing') value += item.pairing_item_ids.length ? 2 : -1;
    return [value, -(minutes ?? 20), -item.id] as const;
  };
  const sorted = [...items].sort((a, b) => {
    const as = score(a); const bs = score(b);
    return bs[0] - as[0] || bs[1] - as[1] || bs[2] - as[2];
  });
  const chosen: MenuItemOut[] = [];
  const seen = new Set<string>();
  for (const item of sorted) {
    if (chosen.length >= limit) break;
    const keyName = category(item, menu);
    if (!seen.has(keyName)) {
      chosen.push(item); seen.add(keyName);
    }
  }
  return chosen;
}

export function buildMockRecommendations(menu: MenuOut, params: RecommendationParams = {}, now = new Date()): RecommendationsOut {
  const limit = Math.min(6, Math.max(2, params.limit ?? 4));
  const partySize = Math.min(10, Math.max(1, params.party_size ?? 1));
  const dietary = params.dietary ?? 'any';
  const spiceMax = Math.min(3, Math.max(0, params.spice_max ?? 3));
  const all = menu.categories.flatMap((entry) => entry.items).filter((item) => item.available);
  // Unrecorded values are unknown, never zero: they cannot satisfy a spice or prep-time limit.
  const filtered = all.filter((item) => dietMatches(item, dietary)
    && (spiceLevel(item) === null ? spiceMax >= 3 : spiceLevel(item)! <= spiceMax)
    && (params.budget_irr === undefined || item.price_irr <= params.budget_irr)
    && (params.max_prep_minutes === undefined || (prepMinutes(item) !== null && prepMinutes(item)! <= params.max_prep_minutes)));
  const pool = filtered;
  const contextValue = context(now);
  const cartIds = (params.cart_item_ids ?? '').split(',').filter(Boolean).map(Number).filter(Number.isFinite);
  const specs: Array<[RecommendationLaneKey, string, string]> = [
    ['moment', 'پیشنهاد مناسب همین لحظه', `بر اساس ${contextValue.daypart_label_fa} و منوی موجود`],
    ['quick', 'سریع و بی‌دردسر', 'برای وقتی که نمی‌خوای زیاد منتظر بمونی'],
    ['popular', 'محبوب‌های مهمان‌ها', 'انتخاب‌هایی که بیشتر سفارش داده شده‌اند'],
    ['seasonal', `تازهٔ فصل ${contextValue.season_label_fa}`, 'طعم‌هایی که حال‌وهوای این فصل را دارند'],
    ['pairing', cartIds.length ? 'سبدت رو کامل کن' : 'ترکیب پیشنهادی', cartIds.length ? 'پیشنهادهای هماهنگ با انتخاب فعلیت' : 'یک انتخاب اصلی، کنار مکمل‌های هماهنگ'],
  ];
  const lanes = specs.map(([key, title, subtitle]) => {
    let ranked = rank(key === 'popular' ? pool.filter((item) => item.popularity_label) : pool, menu, key, contextValue, limit, partySize);
    let pairingBase: MenuItemOut | undefined;
    if (key === 'moment') {
      ranked = rank(momentPool(pool, menu, contextValue), menu, key, contextValue, limit, partySize);
    }
    if (key === 'seasonal') {
      ranked = rank(pool.filter((item) => item.seasonal_offer && (!item.seasonal_label || item.seasonal_label.includes(contextValue.season_label_fa))), menu, key, contextValue, limit, partySize);
    }
    if (key === 'pairing') {
      const cartBase = all.find((item) => cartIds.includes(item.id) && item.pairing_item_ids.length > 0);
      pairingBase = cartBase ?? (cartIds.length ? undefined : ranked.find((item) => item.pairing_item_ids.length > 0));
      if (pairingBase) {
        const paired = pairingBase.pairing_item_ids
          .map((id) => pool.find((item) => item.id === id))
          .filter((item): item is MenuItemOut => item !== undefined)
          .filter((item) => !cartIds.includes(item.id));
        ranked = cartIds.length ? paired.slice(0, limit) : [pairingBase, ...paired.slice(0, limit - 1)];
      } else if (cartIds.length) {
        ranked = [];
      }
    }
    if ((key === 'quick' && !pool.some((item) => prepMinutes(item) !== null))
      || (key === 'pairing' && !all.some((item) => item.pairing_item_ids.length > 0))) return { key, title_fa: title, subtitle_fa: subtitle, items: [] };
    const items = ranked.map((item) => {
      const reasons = key === 'quick'
        ? [`آماده‌سازی حدود ${prepMinutes(item)} دقیقه`]
        : key === 'popular' && item.popularity_label ? [item.popularity_label]
          : key === 'seasonal' && item.seasonal_offer ? [item.seasonal_label ?? `تازهٔ فصل ${contextValue.season_label_fa}`]
            : key === 'pairing' && pairingBase && item.id !== pairingBase.id ? [`هماهنگ با ${pairingBase.name_fa}`]
              : key === 'pairing' ? ['مناسب برای کامل‌کردن سفارش']
              : [contextValue.daypart_label_fa];
      const serves = servesCount(item);
      if (serves !== null && serves >= partySize && partySize > 1) reasons.push(`مناسب ${partySize} نفر`);
      const spice = spiceLevel(item);
      if (spice !== null) reasons.push(spice === 0 ? 'طعم ملایم' : 'طعم پرانرژی');
      return { item, badge_fa: key === 'quick' ? 'سریع و راحت' : key === 'popular' ? 'محبوب مهمان‌ها' : key === 'seasonal' ? 'پیشنهاد فصل' : key === 'pairing' ? 'ترکیب پیشنهادی' : 'متناسب با همین ساعت', reasons_fa: reasons.slice(0, 3) };
    });
    return { key, title_fa: title, subtitle_fa: subtitle, items };
  }).filter((lane) => lane.items.length > 0);
  return { restaurant: menu.restaurant, context: contextValue, lanes };
}
